import React, { useState, useEffect } from 'react';
import './AdminDashboard.css';

const AdminDashboard = ({ user, onLogout }) => {
  const [activeTab, setActiveTab] = useState('courses');
  const [courses, setCourses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [students, setStudents] = useState([]);

  // Course form state
  const [courseForm, setCourseForm] = useState({
    code: '',
    name: '',
    creditHours: '',
    teacherId: ''
  });

  // Teacher form state
  const [teacherForm, setTeacherForm] = useState({
    name: '',
    email: '',
    password: ''
  });

  useEffect(() => {
    // Load data from localStorage
    const savedCourses = JSON.parse(localStorage.getItem('courses') || '[]');
    const savedTeachers = JSON.parse(localStorage.getItem('teachers') || '[]');
    const savedStudents = JSON.parse(localStorage.getItem('students') || '[]');
    
    setCourses(savedCourses);
    setTeachers(savedTeachers);
    setStudents(savedStudents);
  }, []);

  const handleAddCourse = (e) => {
    e.preventDefault();
    const teacher = teachers.find(t => t.id === courseForm.teacherId);
    
    if (!teacher) {
      alert('Please select a valid teacher');
      return;
    }

    const newCourse = {
      id: Date.now().toString(),
      code: courseForm.code,
      name: courseForm.name,
      creditHours: parseInt(courseForm.creditHours),
      teacher: {
        id: teacher.id,
        name: teacher.name,
        email: teacher.email
      }
    };

    const updatedCourses = [...courses, newCourse];
    setCourses(updatedCourses);
    localStorage.setItem('courses', JSON.stringify(updatedCourses));

    // Reset form
    setCourseForm({ code: '', name: '', creditHours: '', teacherId: '' });
    alert('Course added successfully!');
  };

  const handleRemoveCourse = (courseId) => {
    if (window.confirm('Are you sure you want to remove this course? This will also remove it from all enrolled students.')) {
      // Remove course from courses list
      const updatedCourses = courses.filter(course => course.id !== courseId);
      setCourses(updatedCourses);
      localStorage.setItem('courses', JSON.stringify(updatedCourses));

      // Remove course from all students' enrolled courses
      const updatedStudents = students.map(student => ({
        ...student,
        courses: student.courses ? student.courses.filter(course => course.id !== courseId) : []
      }));
      setStudents(updatedStudents);
      localStorage.setItem('students', JSON.stringify(updatedStudents));

      alert('Course removed successfully!');
    }
  };

  const handleAddTeacher = (e) => {
    e.preventDefault();
    const newTeacher = {
      id: Date.now().toString(),
      name: teacherForm.name,
      email: teacherForm.email,
      password: teacherForm.password
    };

    const updatedTeachers = [...teachers, newTeacher];
    setTeachers(updatedTeachers);
    localStorage.setItem('teachers', JSON.stringify(updatedTeachers));

    // Reset form
    setTeacherForm({ name: '', email: '', password: '' });
    alert('Teacher added successfully!');
  };

  const handleRemoveTeacher = (teacherId) => {
    if (window.confirm('Are you sure you want to remove this teacher? This will also remove them from assigned courses.')) {
      // Remove teacher from teachers list
      const updatedTeachers = teachers.filter(teacher => teacher.id !== teacherId);
      setTeachers(updatedTeachers);
      localStorage.setItem('teachers', JSON.stringify(updatedTeachers));

      // Remove teacher from courses and set to unassigned
      const updatedCourses = courses.map(course => {
        if (course.teacher && course.teacher.id === teacherId) {
          return {
            ...course,
            teacher: null
          };
        }
        return course;
      });
      setCourses(updatedCourses);
      localStorage.setItem('courses', JSON.stringify(updatedCourses));

      alert('Teacher removed successfully!');
    }
  };

  return (
    <div className="admin-dashboard">
      {/* Header */}
      <header className="admin-header">
        <div className="header-left">
          <h1>Admin Dashboard</h1>
          <p>Welcome, {user?.name}</p>
        </div>
        <button onClick={onLogout} className="logout-btn">Logout</button>
      </header>

      {/* Navigation */}
      <nav className="admin-nav">
        <button 
          className={activeTab === 'courses' ? 'nav-btn active' : 'nav-btn'}
          onClick={() => setActiveTab('courses')}
        >
          Courses
        </button>
        <button 
          className={activeTab === 'teachers' ? 'nav-btn active' : 'nav-btn'}
          onClick={() => setActiveTab('teachers')}
        >
          Teachers
        </button>
        <button 
          className={activeTab === 'students' ? 'nav-btn active' : 'nav-btn'}
          onClick={() => setActiveTab('students')}
        >
          Students
        </button>
      </nav>

      {/* Content */}
      <main className="admin-content">
        {activeTab === 'courses' && (
          <div className="tab-content">
            <h2>Manage Courses</h2>
            
            {/* Add Course Form */}
            <form onSubmit={handleAddCourse} className="form-card">
              <h3>Add New Course</h3>
              <div className="form-row">
                <div className="form-group">
                  <label>Course Code</label>
                  <input
                    type="text"
                    value={courseForm.code}
                    onChange={(e) => setCourseForm({...courseForm, code: e.target.value})}
                    placeholder="e.g., CS101"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Course Name</label>
                  <input
                    type="text"
                    value={courseForm.name}
                    onChange={(e) => setCourseForm({...courseForm, name: e.target.value})}
                    placeholder="e.g., Data Structures"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Credit Hours</label>
                  <input
                    type="number"
                    value={courseForm.creditHours}
                    onChange={(e) => setCourseForm({...courseForm, creditHours: e.target.value})}
                    placeholder="3"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Assign Teacher</label>
                  <select
                    value={courseForm.teacherId}
                    onChange={(e) => setCourseForm({...courseForm, teacherId: e.target.value})}
                    required
                  >
                    <option value="">Select Teacher</option>
                    {teachers.map(teacher => (
                      <option key={teacher.id} value={teacher.id}>
                        {teacher.name} ({teacher.email})
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <button type="submit" className="submit-btn">Add Course</button>
            </form>

            {/* Courses List */}
            <div className="list-card">
              <h3>Existing Courses ({courses.length})</h3>
              {courses.length === 0 ? (
                <p className="no-data">No courses added yet</p>
              ) : (
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Course Code</th>
                      <th>Course Name</th>
                      <th>Credit Hours</th>
                      <th>Teacher</th>
                      <th>Enrolled Students</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {courses.map(course => {
                      const enrolledCount = students.filter(student => 
                        student.courses?.some(c => c.id === course.id)
                      ).length;
                      
                      return (
                        <tr key={course.id}>
                          <td>{course.code}</td>
                          <td>{course.name}</td>
                          <td>{course.creditHours}</td>
                          <td>
                            {course.teacher ? (
                              <div>
                                <div>{course.teacher.name}</div>
                                <div className="teacher-email">{course.teacher.email}</div>
                              </div>
                            ) : (
                              <span className="not-assigned">Not assigned</span>
                            )}
                          </td>
                          <td>
                            <span className={`enrolled-count ${enrolledCount > 0 ? 'has-students' : ''}`}>
                              {enrolledCount} student(s)
                            </span>
                          </td>
                          <td>
                            <button 
                              onClick={() => handleRemoveCourse(course.id)}
                              className="remove-btn"
                              title="Remove Course"
                            >
                              🗑️ Remove
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {activeTab === 'teachers' && (
          <div className="tab-content">
            <h2>Manage Teachers</h2>
            
            {/* Add Teacher Form */}
            <form onSubmit={handleAddTeacher} className="form-card">
              <h3>Add New Teacher</h3>
              <div className="form-row">
                <div className="form-group">
                  <label>Full Name</label>
                  <input
                    type="text"
                    value={teacherForm.name}
                    onChange={(e) => setTeacherForm({...teacherForm, name: e.target.value})}
                    placeholder="e.g., Qamar Zaman"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={teacherForm.email}
                    onChange={(e) => setTeacherForm({...teacherForm, email: e.target.value})}
                    placeholder="e.g., qamar@gmail.com"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Password</label>
                  <input
                    type="text"
                    value={teacherForm.password}
                    onChange={(e) => setTeacherForm({...teacherForm, password: e.target.value})}
                    placeholder="Set login password"
                    required
                  />
                </div>
              </div>
              <button type="submit" className="submit-btn">Add Teacher</button>
            </form>

            {/* Teachers List */}
            <div className="list-card">
              <h3>Existing Teachers ({teachers.length})</h3>
              {teachers.length === 0 ? (
                <p className="no-data">No teachers added yet</p>
              ) : (
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Password</th>
                      <th>Assigned Courses</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {teachers.map(teacher => {
                      const assignedCourses = courses.filter(course => 
                        course.teacher && course.teacher.id === teacher.id
                      );
                      
                      return (
                        <tr key={teacher.id}>
                          <td>{teacher.name}</td>
                          <td>{teacher.email}</td>
                          <td className="password-cell">{teacher.password}</td>
                          <td>
                            {assignedCourses.length > 0 ? (
                              <div>
                                {assignedCourses.map(course => (
                                  <div key={course.id} className="assigned-course">
                                    {course.code} - {course.name}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <span className="no-courses">No courses assigned</span>
                            )}
                          </td>
                          <td>
                            <button 
                              onClick={() => handleRemoveTeacher(teacher.id)}
                              className="remove-btn"
                              title="Remove Teacher"
                              disabled={assignedCourses.length > 0}
                            >
                              🗑️ Remove
                            </button>
                            {assignedCourses.length > 0 && (
                              <div className="warning-text">
                                Unassign courses first
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {activeTab === 'students' && (
          <div className="tab-content">
            <h2>Student Registrations ({students.length})</h2>
            <div className="list-card">
              {students.length === 0 ? (
                <p className="no-data">No student registrations yet</p>
              ) : (
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Student Name</th>
                      <th>Email</th>
                      <th>Registered Courses</th>
                      <th>Registration Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {students.map(student => (
                      <tr key={student.id}>
                        <td>{student.name}</td>
                        <td>{student.email}</td>
                        <td>
                          {student.courses?.length > 0 ? (
                            <div className="courses-list">
                              {student.courses.map(course => (
                                <div key={course.id} className="student-course">
                                  <strong>{course.code}</strong> - {course.name}
                                  {course.teacher && (
                                    <span className="course-teacher"> (Teacher: {course.teacher.name})</span>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <span className="no-courses">No courses registered</span>
                          )}
                        </td>
                        <td>
                          {new Date(parseInt(student.id)).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;